/**
 * Camera permission utilities.
 *
 * Wraps the Permissions API (where available) with a getUserMedia fallback so
 * the widget can gate the camera flow without surprising the user.
 *
 * Browser compatibility notes:
 *  - navigator.permissions is unavailable in Safari < 16 and some WebViews.
 *  - navigator.mediaDevices is unavailable on HTTP (non-secure) origins.
 *  - `isCameraHardBlocked` is intentionally synchronous so it can be called
 *    during widget init without awaiting.
 */

import type { MediaPort } from './ports/media-port.js';
import { BrowserMediaPort } from './ports/browser-media-port.js';

/** The resolved camera permission state. */
export type CameraPermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

/**
 * Alias kept for backwards compatibility with code that imported PermissionStatus.
 * @deprecated Use CameraPermissionState instead.
 */
export type PermissionStatus = 'granted' | 'denied' | 'prompt';

/**
 * Checks the current camera permission state without triggering a browser
 * prompt.
 *
 * Falls back to 'unknown' when the Permissions API is unavailable (e.g. older
 * Safari < 16, some WebViews). Returns 'unknown' rather than the old 'prompt'
 * so callers can distinguish "we don't know yet" from "user has not decided".
 *
 * @returns The current permission state.
 */
export async function checkCameraPermission(): Promise<CameraPermissionState> {
  // Hard-blocked if the getUserMedia API is absent entirely
  if (isCameraHardBlocked()) {
    return 'denied';
  }

  if (!navigator.permissions) {
    // Permissions API not supported (Safari < 16, some Android WebViews).
    // We cannot query without prompting — return 'unknown'.
    return 'unknown';
  }

  try {
    const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
    // The Permissions API state values map 1:1 to our type
    return result.state as CameraPermissionState;
  } catch {
    // Some browsers throw for unrecognised permission names (e.g. Firefox < 131
    // does not support the 'camera' permission name).
    return 'unknown';
  }
}

/**
 * Actively requests camera permission by briefly opening a getUserMedia
 * stream and immediately closing it.
 *
 * Use this to trigger the browser permission dialog before the full try-on
 * modal appears, so the user can grant access proactively.
 *
 * @returns true if permission was granted, false if denied or unavailable.
 */
export async function requestCameraPermission(mediaPort?: MediaPort): Promise<boolean> {
  if (isCameraHardBlocked()) {
    return false;
  }

  const port = mediaPort ?? new BrowserMediaPort();

  try {
    const stream = await port.getUserMedia({ video: true, audio: false });
    // Immediately stop all tracks — we only needed to trigger the permission prompt.
    for (const track of stream.getTracks()) {
      track.stop();
    }
    return true;
  } catch (err) {
    if (err instanceof DOMException) {
      // NotAllowedError / PermissionDeniedError = user explicitly denied
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        return false;
      }
      // NotFoundError = no camera hardware (also counts as blocked for UX)
      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        return false;
      }
      // NotReadableError = camera in use by another application
      if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        return false;
      }
    }
    // Unexpected error — treat as unavailable
    return false;
  }
}

/**
 * Synchronously checks whether camera access is structurally impossible in
 * the current context, regardless of user permission state.
 *
 * Returns true if:
 *  - The page is served over HTTP (non-secure origin) — getUserMedia is
 *    blocked by browsers on non-HTTPS pages.
 *  - The browser does not implement the mediaDevices API at all.
 *  - navigator.mediaDevices.getUserMedia is not a function.
 *
 * Note: this does NOT check the user's permission choice. A return value of
 * false means the API is available but the user may still deny it.
 *
 * @returns true if the camera API is structurally unavailable.
 */
export function isCameraHardBlocked(): boolean {
  // No navigator or mediaDevices at all
  if (
    typeof navigator === 'undefined' ||
    !navigator.mediaDevices ||
    typeof navigator.mediaDevices.getUserMedia !== 'function'
  ) {
    return true;
  }
  return false;
}
